import { forwardRef, type ElementType, type ReactNode } from 'react';
import classNames from 'classnames/bind';
import { ColorsWhite } from '@fff-web/design-tokens/colors';
import type { PolymorphicElementRef } from '@fff-web/ui-components';
import type { ModuleWrapperElement, ModuleWrapperElementProps } from './types';
import styles from './ModuleWrapper.module.scss';

const cx = classNames.bind(styles);

/**
 * A full width module wrapper. This component is polymorphic so
 * by passing another element name in the `as` prop, you can change
 * the underlying HTML element.
 *
 * The data-theme is hardcoded to light to indicate that text
 * should be black by default. Feel free to update as needed,
 * for example by passing a background color to the wrapper
 * you use a function to detemrine if the theme should be light or dark.
 *
 * The style property passing a background color is also harcoding the
 * value, but if you pass a bacground color via props, you can
 * define the bg of the module dynamically. This is useful when you
 * want to let content-authors to define a background color via CMS.
 */
export const ModuleWrapper: ModuleWrapperElement = forwardRef(function ModuleWrapper<
  T extends ElementType = 'section',
>(
  {
    as,
    className,
    children,
    isLast,
    moduleId,
    theme,
    backgroundColor = ColorsWhite,
    style,
    id,
    ...props
  }: ModuleWrapperElementProps<T>,
  ref: PolymorphicElementRef<T extends unknown ? 'section' : T>,
): ReactNode {
  const Element = as ?? 'section';

  return (
    <Element
      id={id ?? moduleId}
      ref={ref}
      className={cx('module-wrapper', { 'module-wrapper--last': isLast }, className)}
      {...props}
      data-theme={theme}
      style={{ backgroundColor, ...style }}
    >
      {children}
    </Element>
  );
});
